// angular dependencies
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

// third-party dependencies
import { Store } from '@ngrx/store';
import {
  DataUrl,
  NgxImageCompressService,
  UploadResponse,
} from 'ngx-image-compress';
import { Subscription } from 'rxjs';

// fantaz dependencies
import { AuthFacade } from '@fantaz/shared/auth';
import { User } from '@fantaz/shared/data-access/models';
import { SnackbarService } from '@fantaz/shared/snackbar';
import { MaterialModule } from '@fantaz/shared/ui/material';
import {
  UploadPictureActions,
  UploadPictureFacade,
} from '@fantaz/user-verification/data-access';

const UPLOAD_ICON = `
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
  <g id="cloud_upload" clip-path="url(#clip0_3625_175881)">
  <path d="M19.85 10.04C19.17 6.59 16.14 4 12.5 4C9.61 4 7.1 5.64 5.85 8.04C2.84 8.36 0.5 10.91 0.5 14C0.5 17.31 3.19 20 6.5 20H19.5C22.26 20 24.5 17.76 24.5 15C24.5 12.36 22.45 10.22 19.85 10.04ZM19.5 18H6.5C4.29 18 2.5 16.21 2.5 14C2.5 11.95 4.03 10.24 6.06 10.03L7.13 9.92L7.63 8.97C8.58 7.14 10.44 6 12.5 6C15.12 6 17.38 7.86 17.89 10.43L18.19 11.93L19.72 12.04C21.28 12.14 22.5 13.45 22.5 15C22.5 16.65 21.15 18 19.5 18ZM8.5 13H11.05V16H13.95V13H16.5L12.5 9L8.5 13Z" fill="#FF7D26"/>
  </g>
  <defs>
  <clipPath id="clip0_3625_175881">
  <rect width="24" height="24" fill="white" transform="translate(0.5)"/>
  </clipPath>
  </defs>
  </svg>
`;

@Component({
  selector: 'fantaz-upload-image-dialog',
  templateUrl: './upload-image-dialog.component.html',
  styleUrls: ['./upload-image-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MaterialModule],
})
export class UploadImageDialogComponent implements OnInit, OnDestroy {
  snackBar = inject(SnackbarService);
  verifying$ = this.uploadFacade.isVerifyingPicture$;
  verified$ = this.uploadFacade.isPictureVerified$;
  uploading$ = this.uploadFacade.isUploadingPicture$;
  error$ = this.uploadFacade.errorUploadingPicture$;
  currentUser$ = this.authFacade.authUser$;
  imgSrc: string;
  error: string;
  file?: File;
  fileSize?: string;
  user: User;
  verifying = false;
  uploading = false;
  fileUploaded = false;
  minImageSize = {
    width: 600,
    height: 300,
  };
  private subscriptions: Subscription[] = [];
  private imageCompress = inject(NgxImageCompressService);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public dialogRef: MatDialogRef<UploadImageDialogComponent>,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private store: Store,
    private uploadFacade: UploadPictureFacade,
    private authFacade: AuthFacade,
    private cd: ChangeDetectorRef,
  ) {
    iconRegistry.addSvgIconLiteral(
      'upload-icon',
      sanitizer.bypassSecurityTrustHtml(UPLOAD_ICON),
    );
  }

  ngOnInit(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: 'verification-image' },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
    this.subscriptions.push(
      this.currentUser$.subscribe((user) => {
        this.user = user as User;
      }),
      this.error$.subscribe((message) => {
        this.error = message as string;
      }),
      this.uploading$.subscribe((uploading) => {
        this.uploading = uploading;
      }),
      this.verifying$.subscribe((verifying) => {
        this.verifying = verifying;
      }),
      this.verified$.subscribe((verified) => {
        if (verified) {
          this.onClose();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: null },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.uploadFacade.cleanState$();
    this.file = undefined;
    this.fileSize = undefined;
    this.fileUploaded = false;
  }

  async verifyImage() {
    if (this.error) {
      return;
    }
    if (this.file) {
      const copyFile = new File([this.file], `${this.user.email}.jpg`, {
        type: 'image/jpeg',
      });
      this.uploadFacade.uploadToS3(copyFile);
    }
  }

  validateImage() {
    if (this.file) {
      this.uploadFacade.validateImage();
    } else {
      this.snackBar.showSnackbar(
        'You need to upload an image of your id to verify your identity.',
      );
    }
  }

  uploadImage(): Promise<void> {
    this.store.dispatch(UploadPictureActions.cleanError());
    return this.imageCompress
      .uploadFile()
      .then(async ({ image, orientation, fileName }: UploadResponse) => {
        console.warn('Size in bytes was:', this.imageCompress.byteCount(image));

        if (this.imageCompress.byteCount(image) < 20000000) {
          const img = new Image();
          img.src = image;
          await new Promise((resolve) => (img.onload = resolve));

          if (
            img.width < this.minImageSize.width ||
            img.height < this.minImageSize.height
          ) {
            this.store.dispatch(
              UploadPictureActions.uploadPictureFailure({
                error: 'image-size',
              }),
            );
            return;
          }

          const originalWidth = img.width;
          const originalHeight = img.height;
          let newWidth = originalWidth;
          let newHeight = originalHeight;
          let resizeTo = 100;

          if (originalWidth > 1400 && originalHeight > 700) {
            console.warn(
              'Compressing and resizing to width 900px height 600px...',
            );
            newWidth = 900;
            newHeight = 600;
            resizeTo = 50;
          }

          this.imageCompress
            .compressFile(
              image,
              orientation,
              resizeTo,
              resizeTo,
              newWidth,
              newHeight,
            )
            .then((result: DataUrl) => {
              const blob = this.dataURLtoBlob(result);
              this.file = new File(
                [blob],
                fileName.replace(/\.[^/.]+$/, '') + '.jpg',
                {
                  type: 'image/jpeg',
                },
              );
              this.fileSize = (this.file.size / (1024 * 1024)).toFixed(2);
              this.displayImage();
              this.fileUploaded = true;
              setTimeout(() => {
                this.cd.detectChanges();
              }, 1000);
            });
        } else {
          this.snackBar.showSnackbar('Image size should be less than 20 MB');
          this.store.dispatch(
            UploadPictureActions.uploadPictureFailure({
              error: 'Invalid Size Image',
            }),
          );
          return;
        }
      });
  }

  dataURLtoBlob(dataURL: string): Blob {
    const [metadata, base64Data] = dataURL.split(',');
    const contentType = metadata.split(':')[1].split(';')[0];
    const binary = atob(base64Data);
    const dataArray = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      dataArray[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([dataArray], { type: contentType });

    return blob;
  }

  onClose() {
    this.dialogRef.close({
      source: 'button',
    });
  }

  get otherElements() {
    return this.error || this.file;
  }

  get isVerifying() {
    return this.uploading || this.verifying;
  }

  displayImage(): void {
    if (this.file) {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.imgSrc = e.target?.result as string;
      };

      reader.readAsDataURL(this.file);
    }
  }

  getErrorText(error: string) {
    if (error === 'text-match') {
      return "The details on your ID don't match the info on your player profile. Please take a clear photo, with readable text, and try again.";
    }
    if (error === 'face-blur') {
      return 'The photo on your ID is not clear. Please ensure your face is clearly visible in the photo and try again.';
    }
    if (error === 'image-blur') {
      return 'The image of your ID is blurry. Please take a clear photo in good lighting and try again.';
    }
    if (error === 'image-size') {
      return `Please upload an image with dimensions at least ${this.minImageSize.width}px X ${this.minImageSize.height}px for accurate verification.`;
    }
    return 'There seems to be a problem with uploading your ID. Please check your internet connection and try again.';
  }
}
